import type { ReactNode } from 'react';
import { fitValueFontSize } from '../kpiFit.js';

export interface DefaultKpiCardProps {
  label: string;
  value: ReactNode;
  /** Optional change indicator — renders as a tinted pill next to the value. */
  delta?: { value: string; direction: 'up' | 'down' | 'flat' };
  /** Muted secondary line below the value (e.g. "vs. yesterday"). */
  meta?: string;
  /** Staggers the mount fade-up across a KPI row. */
  delayMs?: number;
  /** KpiCard `fit`: the value's length, for one-line sizing to the tile (../kpiFit.ts). */
  fitChars?: number;
}

/**
 * Clean-SaaS KPI card for the default theme. White surface, tight
 * padding, 1px border, small tinted delta pill on the right, optional
 * meta line below. Reads entirely from `--evi-*` tokens, so the same
 * component renders correctly in both default-light and default-dark.
 */
export function DefaultKpiCard({ label, value, delta, meta, delayMs = 0, fitChars }: DefaultKpiCardProps) {
  const fit = fitChars !== undefined;
  return (
    <div
      style={{
        background: 'var(--evi-surface)',
        border: '1px solid var(--evi-border)',
        borderRadius: 'var(--evi-radius-lg)',
        padding: '16px 18px',
        boxShadow: 'var(--evi-card-glow)',
        opacity: 0,
        animation: `evi-fade-up 0.5s var(--evi-motion-fill) ${delayMs}ms forwards`,
        // Fill the parent cell + center the label / value / delta
        // stack inside it. Without these the card sat at content
        // height and the stack pinned to the top of an oversized
        // grid cell. minWidth: 0 lets the value text shrink/wrap
        // when the column gets narrow; overflow:hidden + the
        // value's wordBreak below keep a single long numeric
        // from poking out the right edge.
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        minWidth: 0,
        overflow: 'hidden',
        boxSizing: 'border-box',
        // fit: the value below sizes itself to this card's content box.
        ...(fit ? { containerType: 'inline-size' as const } : {}),
      }}
    >
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          color: 'var(--evi-text-mute)',
          fontWeight: 500,
          // 8 -> 4: reclaims 4px of vertical breathing so the
          // value + delta stack fits the 2-row dashboard cell
          // (~68px of card content area after widget + card
          // padding) without `overflow: hidden` clipping the
          // delta pill.
          marginBottom: 4,
          // fit: a label too long for one line wraps at a word, two
          // even lines at a tighter line height.
          ...(fit ? { marginBottom: 2, lineHeight: 1.2, textWrap: 'balance' as const, maxWidth: '100%' } : {}),
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: 10,
          marginBottom: meta ? (fit ? 3 : 6) : 0,
          maxWidth: '100%',
        }}
      >
        <div
          style={{
            // 26 -> 22 so the value stays on one line at the
            // 2-row dashboard widget width (cell ~165px wide
            // after widget + card padding, content area ~100px).
            // At 26px "225.6 kWh" wrapped to two lines and the
            // delta pill clipped under `overflow: hidden`; at
            // 22px the value fits in one line and the stack
            // sits comfortably in the 68px card content area.
            fontSize: 22,
            fontWeight: 600,
            color: 'var(--evi-text)',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.5px',
            // lineHeight 1.1 stays so wrapped values (long
            // unit suffixes on narrower future cells) still
            // have between-line breathing room.
            lineHeight: 1.1,
            wordBreak: 'break-word',
            minWidth: 0,
            // fit: one line, never broken mid-number, sized to the tile.
            ...(fitChars !== undefined
              ? { fontSize: fitValueFontSize(fitChars, 22), whiteSpace: 'nowrap' as const, wordBreak: 'normal' as const }
              : {}),
          }}
        >
          {value}
        </div>
        {delta && <DeltaPill value={delta.value} direction={delta.direction} />}
      </div>
      {meta && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--evi-text-mute)',
            // fit: wraps at a space only (a figure has no break point), two even lines.
            ...(fit ? { fontSize: 11, lineHeight: 1.2, textWrap: 'balance' as const, maxWidth: '100%' } : {}),
          }}
        >
          {meta}
        </div>
      )}
    </div>
  );
}

function DeltaPill({ value, direction }: { value: string; direction: 'up' | 'down' | 'flat' }) {
  const bg =
    direction === 'up'
      ? 'var(--evi-accent-soft)'
      : direction === 'down'
        ? 'rgba(220, 38, 38, 0.10)'
        : 'var(--evi-surface-2)';
  const fg =
    direction === 'up'
      ? 'var(--evi-good)'
      : direction === 'down'
        ? 'var(--evi-warn)'
        : 'var(--evi-text-mute)';
  const arrow = direction === 'up' ? '↑' : direction === 'down' ? '↓' : '→';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        fontSize: 11,
        fontWeight: 500,
        padding: '3px 7px',
        borderRadius: 999,
        background: bg,
        color: fg,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {arrow} {value}
    </span>
  );
}
